import axios from 'axios'

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:5000/api'

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
})

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const requestUrl = String(error.config?.url || '')
    const isLoginAttempt = requestUrl.includes('/auth/login')

    // Failed login must stay on the form and show an error — never hard-reload.
    if (status === 401 && !isLoginAttempt) {
      localStorage.removeItem('token')
      delete apiClient.defaults.headers.common['Authorization']

      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong') {
  const err = error as {
    code?: string
    message?: string
    response?: {
      status?: number
      data?: {
        message?: string
        errors?: Array<{ msg?: string; message?: string; path?: string }>
      }
    }
  }

  if (!err?.response) {
    if (err?.code === 'ECONNABORTED') {
      return 'The server took too long to respond. Please try again.'
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return 'You appear to be offline. Check your internet connection.'
    }
    return 'Unable to reach the server. Please try again in a moment.'
  }

  const status = err.response.status
  const data = err.response.data
  const fieldError = data?.errors?.[0]?.msg || data?.errors?.[0]?.message

  if (status === 401) {
    return data?.message || 'Invalid email or password. Please try again.'
  }
  if (status === 403) {
    return data?.message || 'You do not have permission to do that.'
  }
  if (status === 404) {
    return data?.message || 'The requested resource was not found.'
  }
  if (status === 422 || status === 400) {
    return fieldError || data?.message || 'Please check the form and try again.'
  }
  if (status && status >= 500) {
    return data?.message || 'The server ran into a problem. Please try again shortly.'
  }

  return fieldError || data?.message || err.message || fallback
}
